import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { delay, map, of, switchMap, take, tap } from 'rxjs';
import {
  CusValidators,
  ENVIRONMENT,
  ErrorMessagePipe,
  NotificationService,
  WINDOW,
} from 'wflow-cdk';
import { NavigationAccountDialogComponent } from 'wflow-features/navigation/components/navigation-account-dialog/navigation-account-dialog.component';
import { NavigationAccountService } from 'wflow-features/navigation/services/navigation-account.service';
import { ProductEventTrackingService } from 'wflow-features/product-analytics';
import {
  ColorMode,
  ColorModeService,
} from 'wflow-main-app/src/app/features/color-mode';
import { UnsavedFormChangesDirective } from 'wflow-main-app/src/app/shared/directives/unsaved-form-changes.directive';
import { LANGUAGES } from 'wflow-main-app/src/app/shared/__const/global/localization-options.const';
import {
  selectUserAccount,
  selectUserCurrentProfile,
  selectUserLanguage,
} from 'wflow-main-app/src/app/store/user/user.selectors';
import {
  ControlOptionValue,
  DialogComponentsModule,
  DialogInfoComponent,
  InputSelectComponent,
  WfDialogService,
  WuiButtonModule,
  WuiDialog,
  IconsModule,
  WuiTooltipModule,
  WfAvatarComponent,
} from 'wflow-ui';
import { OrganizationAPIService } from '../../api/api/organization';
import { LAST_LANG_STORAGE_KEY } from 'wflow-main-app/src/app/configs/translate.const';
import { NgOptimizedImage } from '@angular/common';
import { UserIdentityAPIService } from '../../api';

@Component({
  selector: 'wf-profile-details-dialog',
  imports: [
    TranslateModule,
    DialogComponentsModule,
    IconsModule,
    WuiTooltipModule,
    WfAvatarComponent,
    WuiButtonModule,
    InputSelectComponent,
    ErrorMessagePipe,
    ReactiveFormsModule,
    NgOptimizedImage,
    UnsavedFormChangesDirective,
  ],
  templateUrl: './profile-details-dialog.component.html',
  styleUrls: ['./profile-details-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'wf-profile-details-dialog no-overflow',
  },
})
export class ProfileDetailsDialogComponent implements OnInit {
  private readonly environment = inject(ENVIRONMENT);
  private readonly window = inject(WINDOW);
  private readonly store = inject(Store);
  private readonly navigationAccountSvc = inject(NavigationAccountService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogSvc = inject(WfDialogService);
  private readonly translateSvc = inject(TranslateService);
  private readonly colorModeSvc = inject(ColorModeService);
  private readonly organizationSvc = inject(OrganizationAPIService);
  private readonly userIdentitySvc = inject(UserIdentityAPIService);
  private readonly notificationSvc = inject(NotificationService);
  private readonly dialogRef = inject(WuiDialog);
  private readonly productTrackingSvc = inject(ProductEventTrackingService);

  private readonly user = this.store.selectSignal(selectUserAccount);
  private readonly profile = this.store.selectSignal(selectUserCurrentProfile);
  private readonly userLanguage = this.store.selectSignal(selectUserLanguage);
  private readonly colorMode = toSignal(this.colorModeSvc.state$);

  public languageOptions = signal<ControlOptionValue<string>[]>([...LANGUAGES]);

  readonly avatarBlobUrl = signal<string | null>(null);

  readonly _user = computed(() => {
    const user = this.user();
    const blobUrl = this.avatarBlobUrl();

    return { ...user, avatar: blobUrl || '' };
  });

  profileForm = new FormGroup({
    colorMode: new FormControl<ColorMode>('auto'),
    language: new FormControl<string>('', [CusValidators.required]),
  });

  constructor() {
    this.navigationAccountSvc
      .myAvatarUrl$(toObservable(this.user).pipe(map((u) => u?.login)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((url) => {
        const oldUrl = this.avatarBlobUrl();
        this.avatarBlobUrl.set(url);
        if (oldUrl) {
          URL.revokeObjectURL(oldUrl);
        }
      });
  }

  ngOnInit() {
    this.destroyRef.onDestroy(() => {
      const currentUrl = this.avatarBlobUrl();
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    });

    this.profileForm.controls.colorMode.patchValue(this.colorMode() ?? 'auto');

    const language = this.userLanguage();

    if (language === 'od') {
      const languageOptions = [...this.languageOptions()];

      languageOptions.unshift({
        value: 'od',
        label: 'Profile_OrganizationDefaultLanguage',
      });

      this.languageOptions.set(languageOptions);

      this.profileForm.controls.language.patchValue(
        this.profile()?.language || 'od',
      );
      return;
    }

    this.profileForm.controls.language.patchValue(language);
  }

  setColorMode(mode: ColorMode) {
    const control = this.profileForm.controls.colorMode;

    control.markAsDirty();
    control.setValue(mode);
  }

  goToAccountSettings() {
    const accountApi = this.environment?.accountApi;

    if (accountApi) {
      const link = `${accountApi}/Identity/Account/Manage`;
      this.window.open(link, '_blank');
    }
  }

  openAvatarDialog(event: Event) {
    event.stopPropagation();

    this.dialogSvc
      .open(NavigationAccountDialogComponent, {
        title: this.translateSvc.instant('PROFILE_PICTURE.Title'),
        minWidth: '400px',
        maxWidth: '600px',
        maxHeight: '90vh',
        data: {
          user: {
            label: this.user().fullName,
            email: this.user().login,
            ...this.user(),
          },
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  saveChanges() {
    if (this.profileForm.invalid) return;

    const { colorMode, language } = this.profileForm.getRawValue();

    if (colorMode !== this.colorMode()) {
      this.colorModeSvc.setState(colorMode);
      this.dialogRef.beforeCloseHook = null;
      this.dialogRef.close();
    }

    if (this.userLanguage() !== language) {
      // Area A [Customization] — the locale the user switched to, at the Save
      // click (before the request). `od` = follow the organization default.
      this.productTrackingSvc.track('nav_language_switched', language);
      // Area I [Customization] — the catalog lists the same change again under
      // the profile, so both keys fire from this one save.
      this.productTrackingSvc.track('onb_language_changed', language);

      this.userIdentitySvc
        .setLanguage(language)
        .pipe(
          switchMap(() =>
            language === 'od'
              ? this.organizationSvc.getOrganizationSettings().pipe(
                  take(1),
                  map((orgSettings) => orgSettings.defaultLanguage),
                )
              : of(language),
          ),
          tap((language) => {
            this.notificationSvc.success({
              text: this.translateSvc.instant('UserProfile'),
              title: this.translateSvc.instant('Ng_PbSaved'),
            });

            window?.localStorage?.setItem(LAST_LANG_STORAGE_KEY, language);

            this.dialogSvc.open(DialogInfoComponent, {
              data: {
                header: this.translateSvc.instant('Wait'),
                content: this.translateSvc.instant('PageRefreshingPleaseWait'),
              },
            });
          }),
          delay(500),
          tap(() => {
            location.reload();
          }),
        )
        .subscribe();
    }
  }
}
